import {
  TBaseDecryptOptions,
  TCursorDecryptOptions,
  TDecryptOptions,
  TSingleDecryptOptions,
} from '../types/encryption';

import { getStaticDashboardConfig } from '@/hooks/useDashboardConfig';
import { ICursorElements } from '@/store/types/search.types';

import { decryptData } from './helpers/decryptData';

export async function decryptTreezorResponse<T>(response: Response, options?: TBaseDecryptOptions): Promise<T[]>;
export async function decryptTreezorResponse<T>(response: Response, options?: TSingleDecryptOptions): Promise<T>;
export async function decryptTreezorResponse<T>(
  response: Response,
  options?: TCursorDecryptOptions,
): Promise<ICursorElements<T>>;

export async function decryptTreezorResponse<T>(
  response: Response,
  options?: TDecryptOptions,
): Promise<T | T[] | ICursorElements<T> | void> {
  const config = getStaticDashboardConfig();

  const isJson = response.headers.get('content-type')?.includes('application/json');

  if (isJson) {
    const responseCrypted = await response.json();

    if (!response.ok) {
      if (response.status === 404) return;
      const requestId = response.headers.get('x-amzn-requestid') || response.headers.get('x-trz-connect-request-id');
      return Promise.reject({ status: response.status, requestId, ...responseCrypted });
    }

    if ((options as TSingleDecryptOptions)?.isSingle) {
      return responseCrypted as T;
    }

    const dataCrypted = Array.isArray(responseCrypted)
      ? responseCrypted
      : responseCrypted['data'] || responseCrypted[Object.keys(responseCrypted)[0]];
    if (!dataCrypted) return Promise.reject(['Error on handling response pattern "{ <resource> : [] }"']);

    const cursor = responseCrypted.cursor;

    if (!config.api.encryption || options?.shouldSkipEncryption) {
      return (options as TCursorDecryptOptions)?.hasCursor ? { elements: dataCrypted, cursor } : dataCrypted;
    }

    const trzCoEncryptedFields = response.headers.get('x-trzco-encrypt-fields') || '';

    if ((options as TSingleDecryptOptions)?.isSingle) {
      return await decryptData<T>(dataCrypted, trzCoEncryptedFields, dataCrypted.userId || (options?.userId as number));
    }

    if (options?.isSequential) {
      const data: T[] = [];
      for (const elementCrypted of dataCrypted) {
        data.push(await decryptData<T>(elementCrypted, trzCoEncryptedFields, elementCrypted.userId || options?.userId));
      }

      return (options as TCursorDecryptOptions)?.hasCursor ? { elements: data, cursor } : data;
    }

    const data = (await Promise.all(
      dataCrypted.map((elementCrypted: T & { userId: number; metadata?: any }) =>
        decryptData(elementCrypted, trzCoEncryptedFields, (elementCrypted.userId || options?.userId) as number),
      ),
    )) as T[];

    return (options as TCursorDecryptOptions)?.hasCursor ? { elements: data, cursor } : data;
  }
}
